import { Box, Typography } from '@mui/material';
import { format } from 'date-fns';
import i18next from 'i18next';
import { ELogsActionsTypes, LogsListItem } from 'entities/Logs/logsTypes';
import { ColorPalette, ColorPaletteValues } from 'shared/consts/colorPalette';
import { Icon } from 'shared/ui/Icon';
import { Column } from 'shared/ui/tables/_common/types';
import { TableTranslationLabel } from 'shared/ui/tables/_common/ui/TableTranslationLabel';

export type UserLogRow = LogsListItem & { id: string };

const actionConfig: Record<
	ELogsActionsTypes,
	{ icon: string; color: ColorPaletteValues; label: string; status: string }
> = {
	[ELogsActionsTypes.IMPORT_FILE]: {
		icon: 'import',
		color: ColorPalette.royalBlue,
		label: 'co_users.tab_3.import_file',
		status: 'co_users.tab_3.imported',
	},
	[ELogsActionsTypes.EXPORT_FILE]: {
		icon: 'export',
		color: ColorPalette.jungleGreen,
		label: 'co_users.tab_3.export_file',
		status: 'co_users.tab_3.exported',
	},
	[ELogsActionsTypes.EXPORT_FILE_FAILED]: {
		icon: 'export',
		color: ColorPalette.violetRed,
		label: 'co_users.tab_3.export_file_failed',
		status: 'co_users.tab_3.failed',
	},
};

const getStatusDetail = (row?: UserLogRow) => {
	switch (row?.actionType) {
		case ELogsActionsTypes.IMPORT_FILE:
			return i18next.t('co_users.tab_3.from_pc');
		case ELogsActionsTypes.EXPORT_FILE:
			return i18next.t('co_users.tab_3.to_pc');
		case ELogsActionsTypes.EXPORT_FILE_FAILED:
			return row.outcome;
		default:
			return null;
	}
};

export const UserLogsColumns: Array<Column<UserLogRow>> = [
	{
		field: 'actionType',
		commonCellSettings: { style: { minWidth: '250px' } },
		headerCellSettings: {
			renderCell: () => <TableTranslationLabel path="co_users.tab_3.action" />,
		},
		bodyCellSettings: {
			renderCell: (row) => {
				const config = row?.actionType && actionConfig[row.actionType];
				if (!config) return '-';
				return (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
						<Icon id={config.icon} color={config.color} />
						<Typography variant={'body2'} component={'span'} color={ColorPalette.astronaut}>
							{i18next.t(config.label)}
						</Typography>
					</Box>
				);
			},
		},
	},
	{
		field: 'createdAt',
		commonCellSettings: { style: { minWidth: '150px' } },
		headerCellSettings: {
			renderCell: () => <TableTranslationLabel label="create_date" />,
		},
		bodyCellSettings: {
			prepareField: (row) =>
				row?.createdAt ? format(new Date(row.createdAt), 'dd-MM-yyyy HH:mm') : '-',
		},
	},
	{
		field: 'fileName',
		commonCellSettings: { style: { minWidth: '200px', maxWidth: '250px', wordBreak: 'break-word' } },
		headerCellSettings: {
			renderCell: () => <TableTranslationLabel label="file_name" />,
		},
		bodyCellSettings: {
			prepareField: (row) => row?.fileName || '-',
		},
	},
	{
		field: 'status',
		commonCellSettings: { style: { minWidth: '200px' } },
		headerCellSettings: {
			renderCell: () => <TableTranslationLabel label="status" />,
		},
		bodyCellSettings: {
			renderCell: (row) => {
				const config = row?.actionType && actionConfig[row.actionType];
				if (!config) return '-';
				return (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
						<Typography
							component={'span'}
							color={ColorPalette.astronaut}
							sx={{
								fontSize: '8px',
								fontWeight: 600,
								lineHeight: '12px',
								letterSpacing: '1.5px',
							}}
						>
							{i18next.t(config.status)}
						</Typography>
						<Typography variant={'body2'} component={'span'} color={ColorPalette.astronaut}>
							{getStatusDetail(row)}
						</Typography>
					</Box>
				);
			},
		},
	},
];
